﻿import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AdminButton, AdminCard, AdminErrorBanner, AdminFormField, AdminInfoRow, AdminLoadingState, AdminPageLayout, AdminSelect, AdminTextarea } from "@/components/admin";
import { formatDateTime } from "@/lib/format";
import { getSoundEventStatusLabel, getSoundRiskDisplay } from "@/lib/soundDisplay";
import SoundDetailHeader from "./components/SoundDetailHeader";
import SoundSummaryCards from "./components/SoundSummaryCards";
import SoundAnalysisResult from "./components/SoundAnalysisResult";
import SoundReferenceValues from "./components/SoundReferenceValues";
import SoundConnectionInfo from "./components/SoundConnectionInfo";
import SoundRelatedHistory from "./components/SoundRelatedHistory";
import SoundDetailActions from "./components/SoundDetailActions";
import { createSoundAnalysisTuningSample, fetchSoundAnalysisDetail, fetchSoundAnalysisResults } from "../../../lib/api";
import type { SoundAnalysisV2Detail, SoundAnalysisV2ListItem } from "../../../lib/api";
const formatConfidence = (value: number | null) => {
    if (value == null)
        return "-";
    return `${Math.round(Math.max(0, Math.min(value, 1)) * 100)}%`;
};
type TuningCorrectYn = "Y" | "N" | "";
const soundTypeLabel = (item: SoundAnalysisV2ListItem) => item.representativeDisplayLabel ?? item.representativeGroupCode ?? "-";
const soundSubTypeLabel = (item: SoundAnalysisV2ListItem) => item.representativeLabelCode ?? item.representativeModelLabelCode ?? "-";
const sourceLabel = (item: SoundAnalysisV2ListItem) => item.candidateContractVersion;
const derivedStatusLabel = (item: SoundAnalysisV2ListItem) => getSoundEventStatusLabel(item.eventStatusCode);
const SoundAnalysisDetailPage = () => {
    const { recordId } = useParams<{
        recordId: string;
    }>();
    const [detail, setDetail] = useState<SoundAnalysisV2Detail | null>(null);
    const [relatedHistory, setRelatedHistory] = useState<SoundAnalysisV2ListItem[]>([]);
    const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null);
    const [tuningMemo, setTuningMemo] = useState("");
    const [tuningCorrectYn, setTuningCorrectYn] = useState<TuningCorrectYn>("Y");
    const [tuningResultMessage, setTuningResultMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [creatingTuning, setCreatingTuning] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const numericId = Number(recordId);
    const loadDetail = useCallback(async () => {
        if (!Number.isFinite(numericId)) {
            setErrorMessage("잘못된 소리 분석 상세 경로입니다.");
            return;
        }
        setLoading(true);
        setErrorMessage(null);
        try {
            const nextDetail = await fetchSoundAnalysisDetail(numericId);
            const history = await fetchSoundAnalysisResults({ subjectKey: nextDetail.subjectKey });
            setDetail(nextDetail);
            setSelectedCandidateId(nextDetail.candidates[0]?.soundAnalysisEventCandidateId ?? null);
            setRelatedHistory(history
                .sort((left, right) => right.analyzedAt.localeCompare(left.analyzedAt))
                .slice(0, 6));
        }
        catch (error) {
            setDetail(null);
            setRelatedHistory([]);
            setErrorMessage(error instanceof Error ? error.message : "소리 분석 상세 정보를 불러오지 못했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, [numericId]);
    useEffect(() => {
        void loadDetail();
    }, [loadDetail]);
    const handleCreateTuningSample = useCallback(async () => {
        if (!detail || selectedCandidateId == null) {
            setErrorMessage("튜닝 샘플로 저장할 후보를 선택하세요.");
            return;
        }
        const selectedCandidate = detail.candidates.find((candidate) => candidate.soundAnalysisEventCandidateId === selectedCandidateId);
        if (!selectedCandidate?.soundGroupCode || !selectedCandidate.displayLabelCode) {
            setErrorMessage("선택한 후보에 sound group/display label 정보가 없습니다.");
            return;
        }
        setCreatingTuning(true);
        setTuningResultMessage(null);
        setErrorMessage(null);
        try {
            const sample = await createSoundAnalysisTuningSample(detail.soundAnalysisEventId, selectedCandidateId, {
                sampleName: `event-${detail.soundAnalysisEventId}-rank-${selectedCandidate.rank}`,
                testScenario: "ADMIN_SOUND_V2_REVIEW",
                playbackSource: "ADMIN_EVENT_DETAIL",
                expectedSoundGroupCode: selectedCandidate.soundGroupCode,
                expectedDisplayLabelCode: selectedCandidate.displayLabelCode,
                expectedRiskYn: selectedCandidate.riskLevelCode === "HIGH" || selectedCandidate.riskLevelCode === "CRITICAL" ? "Y" : "N",
                correctYn: tuningCorrectYn || null,
                memo: tuningMemo || null,
            });
            setTuningResultMessage(`튜닝 샘플 #${sample.soundAnalysisTuningSampleId} 생성 완료`);
            setTuningMemo("");
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "튜닝 샘플 생성에 실패했습니다.");
        }
        finally {
            setCreatingTuning(false);
        }
    }, [detail, selectedCandidateId, tuningCorrectYn, tuningMemo]);
    const risk = useMemo(() => (detail ? getSoundRiskDisplay(detail.riskLevelCode) : { label: "-", tone: "neutral" as const }), [detail]);
    const selectedCandidate = detail?.candidates.find((candidate) => candidate.soundAnalysisEventCandidateId === selectedCandidateId) ?? null;
    const metricRows = Object.entries(detail?.metric ?? {}).filter(([, value]) => value != null);
    const elderlyDisplayName = detail?.seniorName ?? detail?.subjectKey ?? "식별 정보 없음";
    const elderlyIdLabel = detail?.seniorUserAccountId == null ? "-" : String(detail.seniorUserAccountId);
    const deviceDisplayName = detail?.deviceName ?? (detail?.deviceId == null ? "기기 미지정" : `기기 #${detail.deviceId}`);
    return (<AdminPageLayout title="과거 소리 v2 상세" subtitle={detail ? `${elderlyDisplayName} · ${formatDateTime(detail.analyzedAt)}` : "과거 소리 v2 이벤트 상세"}>
          <div className="mt-5 flex flex-col gap-4">
            <AdminErrorBanner message={errorMessage}/>
            {tuningResultMessage && <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{tuningResultMessage}</div>}
            {loading && <AdminLoadingState message="소리 분석 상세 정보를 불러오는 중입니다."/>}

            {detail && (<>
                <SoundDetailHeader recordId={detail.soundAnalysisEventId} elderlyName={elderlyDisplayName} elderlyIdLabel={elderlyIdLabel} riskLabel={risk.label} riskTone={risk.tone} soundType={soundTypeLabel(detail)} deviceName={deviceDisplayName} analyzedAt={formatDateTime(detail.analyzedAt)}/>

                <SoundSummaryCards soundType={soundTypeLabel(detail)} detailCategory={soundSubTypeLabel(detail)} confidenceLabel={formatConfidence(detail.representativeConfidence)} statusLabel={risk.label} analysisSource={sourceLabel(detail)}/>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <div className="xl:col-span-2 flex flex-col gap-4">
                    <AdminCard className="p-4">
                      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-slate-800">Sound v2 후보 Top10</h3>
                          <p className="mt-1 text-xs text-slate-500">
                            모델 {detail.modelName ?? "-"} {detail.modelVersion ?? ""} · taxonomy {detail.taxonomyVersion ?? "-"} · policy {detail.activationPolicyVersion ?? "-"}
                          </p>
                        </div>
                        <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700">
                          후보 {detail.candidates.length}개
                        </span>
                      </div>
                      <div className="overflow-hidden rounded-2xl border border-slate-100">
                        <div className="grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500">
                          <span className="col-span-1">Rank</span>
                          <span className="col-span-2">YAMNet</span>
                          <span className="col-span-3">표시 라벨</span>
                          <span className="col-span-2">Group</span>
                          <span className="col-span-2">Score</span>
                          <span className="col-span-2">운영</span>
                        </div>
                        {detail.candidates.map((candidate) => {
                          const selected = candidate.soundAnalysisEventCandidateId === selectedCandidateId;
                          return (
                            <AdminButton variant="plain" size="auto"
                              key={candidate.soundAnalysisEventCandidateId}
                              type="button"
                              onClick={() => setSelectedCandidateId(candidate.soundAnalysisEventCandidateId)}
                              className={`grid w-full grid-cols-12 gap-2 px-3 py-2 text-left text-xs transition-colors ${selected ? "bg-violet-50" : "bg-white hover:bg-slate-50"}`}
                            >
                              <span className="col-span-1 font-bold text-slate-700">#{candidate.rank}</span>
                              <span className="col-span-2 text-slate-500">{candidate.labelIndex} · {candidate.modelLabelCode}</span>
                              <span className="col-span-3 font-semibold text-slate-700">{candidate.displayLabelKo ?? candidate.displayLabelCode ?? "-"}</span>
                              <span className="col-span-2 text-slate-500">{candidate.soundGroupCode ?? "-"}</span>
                              <span className="col-span-2 font-bold text-blue-700">{formatConfidence(candidate.score)}</span>
                              <span className="col-span-2 text-slate-500">{candidate.operationalEligibleYn ?? "-"} · {candidate.operationalThreshold ?? "-"}</span>
                            </AdminButton>
                          );
                        })}
                      </div>
                    </AdminCard>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <AdminCard className="p-4">
                        <h3 className="mb-3 text-sm font-bold text-slate-800">음향 메트릭</h3>
                        <div className="space-y-1">
                          {metricRows.map(([key, value]) => (
                            <AdminInfoRow key={key} dense bordered label={key} value={String(value)} />
                          ))}
                          {metricRows.length === 0 && <p className="text-sm text-slate-500">메트릭 정보가 없습니다.</p>}
                        </div>
                      </AdminCard>
                      <AdminCard className="p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="text-sm font-bold text-slate-800">튜닝 샘플 생성</h3>
                          <AdminButton variant="primary" size="sm" onClick={handleCreateTuningSample} disabled={creatingTuning || !selectedCandidate} icon={creatingTuning ? "ri-loader-4-line animate-spin" : "ri-flask-line"}>
                            생성
                          </AdminButton>
                        </div>
                        <div className="space-y-2">
                          <AdminInfoRow dense bordered label="선택 후보" value={selectedCandidate ? `rank ${selectedCandidate.rank} · ${selectedCandidate.displayLabelKo ?? selectedCandidate.displayLabelCode}` : "-"} />
                          <AdminFormField label="정답 여부">
                            <AdminSelect value={tuningCorrectYn} onValueChange={(nextValue) => setTuningCorrectYn(nextValue as TuningCorrectYn)}>
                              <option value="Y">정답</option>
                              <option value="N">오답</option>
                              <option value="">보류</option>
                            </AdminSelect>
                          </AdminFormField>
                          <AdminFormField label="검수 메모">
                            <AdminTextarea value={tuningMemo} onChange={(event) => setTuningMemo(event.target.value)} rows={3} className="text-sm" placeholder="현장 소리, 오탐 사유, 거리/환경 등" />
                          </AdminFormField>
                        </div>
                      </AdminCard>
                    </div>

                    <SoundAnalysisResult rows={[
                { label: "소리 유형", value: soundTypeLabel(detail) },
                { label: "세부 분류", value: soundSubTypeLabel(detail) },
                { label: "파생 상태", value: derivedStatusLabel(detail) },
                { label: "운영 확인", value: risk.label },
                { label: "신뢰도", value: formatConfidence(detail.representativeConfidence) },
                { label: "분석 출처", value: sourceLabel(detail) },
            ]}/>

                    <SoundReferenceValues rows={[
                { label: "모델 라벨", value: detail.representativeModelLabelCode ?? "-" },
                { label: "모델 신뢰도", value: formatConfidence(detail.representativeConfidence) },
                { label: "진단 사유", value: detail.diagnosticReasonCode ?? "-" },
                { label: "품질 상태", value: detail.qualityStatusCode },
                { label: "후보 계약", value: detail.candidateContractVersion },
            ]}/>

                    <SoundConnectionInfo rows={[
                { label: "기기 ID", value: detail.deviceId == null ? "-" : String(detail.deviceId) },
                { label: "어르신 ID", value: elderlyIdLabel },
                { label: "분석 시각", value: formatDateTime(detail.analyzedAt) },
                { label: "목적 코드", value: detail.purposeCode ?? "-" },
            ]}/>

                    {relatedHistory.length > 0 && (<SoundRelatedHistory currentId={detail.soundAnalysisEventId} elderlyName={elderlyDisplayName} records={relatedHistory} getSoundTypeLabel={soundTypeLabel} getRiskMeta={(item) => getSoundRiskDisplay(item.riskLevelCode)} formatDateTime={formatDateTime} formatConfidence={formatConfidence}/>) }
                  </div>

                  <div className="xl:col-span-1">
                    <SoundDetailActions elderlyName={elderlyDisplayName} elderlyId={detail.seniorUserAccountId} deviceId={detail.deviceId} deviceName={deviceDisplayName} statusLabel={risk.label} checkNeeded={risk.tone !== "success"}/>
                  </div>
                </div>
              </>)}
          </div>
    </AdminPageLayout>);
};
export default SoundAnalysisDetailPage;
